import { Banner } from 'slab'
import { type MoneyAmount } from 'types'

import { Balance } from './Balance'
import CostBreakdown from './CostBreakdown'
import PaymentDetails from './PaymentDetails'
import { OrderProductCard } from '../../shared/OrderProductCard'
import { type MarketplaceProduct, type MarketplaceProductVariant } from '../../shared/types'
import { useCardPayment } from '../payments/CardPaymentProvider'
import PurchaseButton from '../PurchaseButton'

type OrderSummaryProps = {
  balance: MoneyAmount;
  product: MarketplaceProduct;
  selectedVariant: MarketplaceProductVariant;
  onBuyNow: () => void;
  isProcessing?: boolean;
  isDisabled?: boolean;
  isMobileView?: boolean;
  error?: string;
}

export const OrderSummary = ({
  balance,
  product,
  selectedVariant,
  onBuyNow,
  isProcessing = false,
  isDisabled = false,
  isMobileView = false,
  error,
}: OrderSummaryProps) => {
  const { cardPaymentsEnabled, selectedPaymentMethod, onRemovePaymentMethod } = useCardPayment()

  const productPrice = selectedVariant.discounted_price ?? selectedVariant.base_price
  const productPriceCents = productPrice ? Math.round(parseFloat(productPrice.value) * 100) : 0

  const balanceCents = balance ? Math.round(parseFloat(balance.value) * 100) : 0
  const remainingBalanceCents = balanceCents - productPriceCents
  const isCoveredByBalance = remainingBalanceCents >= 0

  return (
    <div className={isMobileView ? 'flex flex-col' : 'flex flex-col h-full'}>
      <div className={isMobileView ? 'flex flex-col gap-4 mt-4' : 'flex flex-col mt-24 gap-6 px-6'}>
        <h1 className='heading-sm'>Order summary</h1>
        <div className='flex flex-col gap-4'>
          <OrderProductCard
            productBrand={product.product_brand}
            productSlug={product.slug}
            variantName={selectedVariant.name}
            variantBasePrice={selectedVariant.base_price}
            variantDiscountedPrice={selectedVariant.discounted_price}
            variantThumbnailImageUrl={selectedVariant.thumbnail_image_urls[0]}
          />
          <div className='h-[1px] bg-gray-200' />
          {balance && <Balance balance={balance} remainingBalance={remainingBalanceCents} />}
          {cardPaymentsEnabled && !isCoveredByBalance && (
            <>
              <div className='h-[1px] bg-gray-200' />
              <PaymentDetails
                cardDetails={selectedPaymentMethod?.card
                  ? {
                      brand: selectedPaymentMethod.card.brand,
                      last4: selectedPaymentMethod.card.last4,
                    }
                  : undefined}
                onRemovePaymentMethod={onRemovePaymentMethod}
              />
            </>
          )}
          <div className='h-[1px] bg-gray-200' />
          <CostBreakdown subtotal={productPriceCents / 100} thatchBalance={balanceCents / 100} />
        </div>
        <div className='flex flex-col items-center gap-3'>
          <PurchaseButton
            isDisabled={isDisabled}
            isProcessing={isProcessing}
            coveredByBalance={isCoveredByBalance}
            onCreatePurchase={onBuyNow}
          />
          <p className='text-gray-500 text-center'>Non-refundable once purchased</p>
        </div>
        {error && (
          <Banner type='critical' title='Something went wrong while processing your purchase. Please try again.' />
        )}
      </div>
    </div>
  )
}
